"use client";

import { useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDeleteLevantamento } from "@/lib/api-video";
import type { LevantamentoOut } from "@/types/video";

/**
 * #548 — botão de exclusão (soft-delete) de um levantamento na linha da tabela.
 * Self-contained: dono do próprio Dialog de confirmação + mutation. Espelha o
 * padrão do delete-run (results/page.tsx): 1º clique só ABRE a confirmação, o
 * DELETE só dispara no "Excluir". stopPropagation no gatilho pra não navegar
 * (a linha da tabela tem onRowClick).
 */
export function DeleteLevantamentoButton({
  levantamento,
}: {
  levantamento: LevantamentoOut;
}) {
  const [open, setOpen] = useState(false);
  const deleteMut = useDeleteLevantamento();

  function handleConfirm() {
    deleteMut.mutate(levantamento.id, {
      onSuccess: () => {
        toast.success("Levantamento excluído.");
        setOpen(false);
      },
      onError: () =>
        toast.error("Falha ao excluir levantamento. Tente novamente."),
    });
  }

  return (
    <>
      <Button
        variant="ghost"
        size="icon-sm"
        data-testid={`btn-delete-lev-${levantamento.id}`}
        aria-label={`Excluir levantamento ${levantamento.posto_contagem}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <Trash2 className="h-3.5 w-3.5 text-destructive" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Excluir levantamento?</DialogTitle>
            <DialogDescription>
              O levantamento <strong>{levantamento.posto_contagem}</strong> será
              removido da lista. O registro é mantido internamente para
              auditoria (soft-delete), mas não aparecerá mais aqui.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              data-testid="btn-cancel-delete-lev"
              onClick={() => setOpen(false)}
              disabled={deleteMut.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              data-testid="btn-confirm-delete-lev"
              onClick={handleConfirm}
              disabled={deleteMut.isPending}
            >
              {deleteMut.isPending ? "Excluindo…" : "Excluir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
